import Edit16 from '@carbon/icons-react/es/edit/16';
import Money16 from '@carbon/icons-react/es/money/16';
import { useBoolean } from 'ahooks';
import { RadioChangeEvent, Tooltip } from 'antd';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { InputType } from 'components/atoms/input';
import { SingleLineEllipsis } from 'components/styles';
import { useTransformTabularDataForAgGrid } from 'data/templates/projects';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { ProjectTemplateToolBar } from '../../commons';
import {
  BudgetHoursModal,
  useBudgetedHoursConfigText,
  useBudgetHourFieldsForActiveTemplate,
} from '../commons';
import { ToggleState } from './actions';
import { Grid, useGrid } from './grid';

const BorderForTable = styled.div`
  border-left: 1px solid var(--border-color);
`;

const TableWrapper = styled.div`
  height: calc(100vh - 170px);
  margin-left: var(--layout-02);
  float: left;
  width: calc(100% - calc(1 * var(--layout-02)));
  position: relative;

  ${BorderForTable} {
    position: absolute;
    height: 100%;
    top: 0;
    left: 326px;
  }
`;

const GreenMoneyIcon = styled(Money16)`
  color: var(--green-50);
`;

const Ellipsis = styled.span`
  ${SingleLineEllipsis};
`;

const ButtonWithEllipsis = styled(Button)`
  ${Ellipsis} {
    max-width: 83%;
  }
`;

const BudgetHoursContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 0;
  bottom: 0;
  left: 326px;
  border-top: 1px solid var(--border-color);
  height: var(--height-lg);

  ${ButtonWithEllipsis} {
    max-width: 80%;
  }
`;

export const ProjectTemplatePlanListV2: FC = () => {
  const rowData = useTransformTabularDataForAgGrid();
  const phaseInputRef = useRef<InputType>(null);

  useEffect(() => {
    phaseInputRef?.current?.focus();
  }, []);

  const [mode, setMode] = useState<ToggleState>('expand');
  const { gridApi, onGridReady } = useGrid();

  const [
    isBudgetHoursModalVisible,
    {
      setTrue: showBudgetHoursModalVisible,
      setFalse: hideBudgetHoursModalVisible,
    },
  ] = useBoolean(false);

  const {
    budgetedHoursConfig: bugdetedHoursConfig,
    updateBugdetHoursConfig: updateBugdetHoursField,
  } = useBudgetHourFieldsForActiveTemplate();

  const { tooltip: budgetedHoursTooltip, text: budgetedHoursText } =
    useBudgetedHoursConfigText();

  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );

  const onCollapseChange = (e: RadioChangeEvent) => {
    const toggleMode = e.target.value as ToggleState;

    switch (toggleMode) {
      case 'collapse':
        gridApi?.collapseAll();
        setMode(toggleMode);
        break;
      case 'expand':
        gridApi?.expandAll();
        setMode(toggleMode);
        break;
      default:
    }
  };

  return (
    <>
      <ProjectTemplateToolBar
        data={rowData.filter((_data) => !_data?.data?.isNew)}
        mode={mode}
        expandCollapse={onCollapseChange}
      />
      <TableWrapper>
        <Grid gridApi={gridApi} onGridReady={onGridReady} />
        {isTimesheetFeatureEnabled ? (
          <>
            <BorderForTable />
            {bugdetedHoursConfig != null ? (
              <Tooltip title={budgetedHoursTooltip}>
                <BudgetHoursContainer>
                  <GreenMoneyIcon />
                  <ButtonWithEllipsis
                    type="text"
                    icon={<Edit16 />}
                    iconPlacement="right"
                    onClick={showBudgetHoursModalVisible}
                  >
                    <Ellipsis>{budgetedHoursText}</Ellipsis>
                  </ButtonWithEllipsis>
                </BudgetHoursContainer>
              </Tooltip>
            ) : (
              <BudgetHoursContainer>
                <Button
                  type="link"
                  icon={<Edit16 />}
                  onClick={showBudgetHoursModalVisible}
                >
                  <FormattedMessage id="project_hours_budget" />
                </Button>
              </BudgetHoursContainer>
            )}
            {isBudgetHoursModalVisible ? (
              <BudgetHoursModal
                budgetedHoursConfig={bugdetedHoursConfig}
                save={(value) => {
                  updateBugdetHoursField(value);
                  hideBudgetHoursModalVisible();
                }}
                reset={() => {
                  updateBugdetHoursField();
                  hideBudgetHoursModalVisible();
                }}
                cancel={() => {
                  hideBudgetHoursModalVisible();
                }}
              />
            ) : null}
          </>
        ) : null}
      </TableWrapper>
    </>
  );
};
